import { useEffect, useCallback } from "react";
import Select from "components/ui/Select";
import { useAllOrderPrintFormats } from "http-hooks/ordprintformat/useOrderPrintFormat";
import { SelectVendors, SelectCustomers, SelectTransporters, SelectPriceLists, SelectAgentsAndSalesmen } from "components/ui/selectors";
import OrderNumberInput from "components/order/OrderNumberInput";
import LotModal from "components/order/LotModal";
import Input from "components/ui/Input";
import { useOrderHeader } from "hooks/order";
import useAuth from "hooks/useAuth";
import { ORDER_TYPE_OPTIONS, getOrderTypeOption, getFormType } from "utils/orderHelpers";

export default function SetOrderHeader({ 
  id, 
  title = "Order Form",
  formType: formTypeProp,
  onOpenLoadTemplate,
  onOpenSaveTemplate,
}) {
  const { agttp } = useAuth();

  // Derive formType: AS (Agent Sale), TP (Trader Purchase), TS (Trader Sale)
  const formType = formTypeProp || getFormType(agttp);

  const showCustomer = formType === "AS" || formType === "TS";
  const showVendor = formType === "AS" || formType === "TP";

  // Global order header state
  const { 
    orderHeader, 
    updateOrderHeader, 
    isLotModalOpen, 
    setLotModalOpen,
    selectBoxes,
    updateSelectBox,
    pricelistFilters,
    setPricelistFilters
  } = useOrderHeader();

  // Hotkey: ] to toggle Lot modal, ALT+I to load template, ALT+S to save template
  const handleKeyDown = useCallback((e) => {
    if (e.key === "]" && !e.ctrlKey && !e.metaKey && !e.altKey) {
      const tag = e.target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || e.target.isContentEditable) return;
      e.preventDefault();
      setLotModalOpen(!isLotModalOpen);
    }
    if (e.altKey && (e.key === "i" || e.key === "I")) {
      e.preventDefault();
      onOpenLoadTemplate?.();
    }
    if (e.altKey && (e.key === "s" || e.key === "S")) {
      e.preventDefault();
      onOpenSaveTemplate?.();
    }
  }, [isLotModalOpen, setLotModalOpen, onOpenLoadTemplate, onOpenSaveTemplate]);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);
  
  const { data: allPrintTypes = [] } = useAllOrderPrintFormats();
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    updateOrderHeader({ [name]: value });
  };

  const handleVendorChange = (val) => {
    updateSelectBox('vendor', val);
    setPricelistFilters({ supid: val?.ps_Id || "" });
    updateOrderHeader({ ordvendid: val?.ps_Id || "" });
  };

  const handleCustomerChange = (val) => {
    updateSelectBox('customer', val);
    updateOrderHeader({ ordcusid: val?.ps_Id || "" });
  };

  const handleTransportChange = (val) => {
    updateSelectBox('transporter', val);
    updateOrderHeader({ ordtptid: val?.ps_Id || "" });
  };

  const handleAgentSalesmanChange = (val) => {
    updateSelectBox('agentSalesman', val);
    updateOrderHeader({ agent_salesman_code: val?.ps_Id || "" });
  };

  const handlePrintTypeChange = (val) => {
    updateSelectBox('printType', val);
    updateOrderHeader({ prntp: val?.id || "" });
  };

  const handlePricelistChange = (val) => {
    updateSelectBox('pricelist', val);
    updateOrderHeader({ pldate: val?.effrctivedt || "" });
  };

  return (
    <>
      {/* Lot Modal */}
      <LotModal
        isOpen={isLotModalOpen}
        onClose={() => setLotModalOpen(false)}
      />

      <form className="px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-4">
          <h1 className="font-semibold leading-6 text-3xl text-gray-900">
            {title}
          </h1>
          <div className="flex items-center gap-2">
            {onOpenLoadTemplate && (
              <button
                type="button"
                onClick={onOpenLoadTemplate}
                className="rounded-md bg-gray-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 inline-flex items-center gap-2"
                title="Load order template (Alt+I)"
              >
                Load Template
                <kbd className="hidden sm:inline-flex items-center rounded bg-gray-500 px-1.5 py-0.5 text-xs font-medium text-gray-100">Alt+I</kbd>
              </button>
            )}
            {onOpenSaveTemplate && (
              <button
                type="button"
                onClick={onOpenSaveTemplate}
                className="rounded-md bg-gray-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 inline-flex items-center gap-2"
                title="Save order template (Alt+S)"
              >
                Save Template
                <kbd className="hidden sm:inline-flex items-center rounded bg-gray-500 px-1.5 py-0.5 text-xs font-medium text-gray-100">Alt+S</kbd>
              </button>
            )}
            <button
              type="button"
              onClick={() => setLotModalOpen(true)}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 inline-flex items-center gap-2"
            >
              Lot
              <kbd className="hidden sm:inline-flex items-center rounded bg-blue-500 px-1.5 py-0.5 text-xs font-medium text-blue-100">]</kbd>
            </button>
          </div>
        </div>

        {/* Keyboard shortcut hints */}
        <div className="mb-4 rounded-md bg-blue-50 border border-blue-200 px-4 py-2">
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-blue-700">
            <span>Press <kbd className="font-semibold text-red-600 text-base mx-0.5">]</kbd> for Lot &amp; Case Details</span>
            {onOpenLoadTemplate && (
              <span>Press <kbd className="font-semibold text-red-600 text-base mx-0.5">Alt+I</kbd> to load order template</span>
            )}
            {onOpenSaveTemplate && (
              <span>Press <kbd className="font-semibold text-red-600 text-base mx-0.5">Alt+S</kbd> to save order template</span>
            )}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-3 gap-x-6 gap-y-4">
          {/* Order Number Input Component */}
          <OrderNumberInput
            isEditMode={!!id}
          />
          
          <Input
            label="Order Date"
            name="ordate"
            type="date"
            value={orderHeader.ordate}
            onChange={handleInputChange}
            isRequired
            withinFY
          />

          {showCustomer && (
            <SelectCustomers
              value={selectBoxes.customer}
              onChange={handleCustomerChange}
              className=""
              isRequired={true}
            />
          )}

          {showVendor && (
            <SelectVendors
              value={selectBoxes.vendor}
              onChange={handleVendorChange}
              className=""
              isRequired={true}
            />
          )}

          <SelectTransporters
            value={selectBoxes.transporter}
            onChange={handleTransportChange}
            className=""
          />

          <SelectAgentsAndSalesmen
            value={selectBoxes.agentSalesman}
            onChange={handleAgentSalesmanChange}
            label="Agent/Salesman"
            placeholder="Select Agent or Salesman"
            className=""
          />

          <Input
            label="Despatched At"
            name="orddespatch"
            type="text"
            value={orderHeader.orddespatch}
            onChange={handleInputChange}
          />

          {showVendor && (
            <div className="">
              <SelectPriceLists
                label="Price Date"
                value={selectBoxes.pricelist}
                onChange={handlePricelistChange}
                placeholder="Select price date"
                vendorId={selectBoxes.vendor?.ps_Id}
                autoSelectFirst={agttp === 0}
              />
            </div>
          )}

          <Input
            label="Ordered By"
            name="orderedby"
            type="text"
            value={orderHeader.orderedby}
            onChange={handleInputChange}
            maxLength={30}
          />

          <Select
            label="Print type"
            isRequired
            value={selectBoxes.printType}
            onChange={handlePrintTypeChange}
            options={allPrintTypes}
            getOptionValue={(option) => option?.id}
            getOptionLabel={(option) => option?.name}
            placeholder="Select print type"
            isClearable
          />

          <Input
            label="Private Marka"
            name="private_marka"
            type="text"
            value={orderHeader.private_marka}
            onChange={handleInputChange}
            placeholder="Enter private marka"
            maxLength={15}
          />

          {!!id && (
            <Select
              label="Order Type"
              value={getOrderTypeOption(orderHeader.ordertyp)}
              onChange={(option) => updateOrderHeader({ ordertyp: option?.value || "G" })}
              options={ORDER_TYPE_OPTIONS}
              getOptionValue={(option) => option.value}
              getOptionLabel={(option) => option.label}
              placeholder="Select order type"
              isClearable={false}
            />
          )}

        </div>
      </form>
    </>
  );
}
